import { create } from 'zustand';
import { readStorage, writeStorage } from '../../lib/safeStorage';
import { StorageKey } from '../../lib/storageKeys';

/**
 * Gesture learning counters (ui-redesign §3.6.2, data dictionary §7): how often each lane
 * gesture was committed on this device. Learning hints (ghost second line, block tips) retire
 * once a gesture reaches its threshold. Local only — never synchronised, never displayed.
 */

export const GestureKind = {
  CreateDrag: 'create-drag',
  CreateClick: 'create-click',
  Move: 'move',
  Resize: 'resize',
  /** Quick mark entered Locked through a tile gesture (double activation or long press, §4.2). */
  QuickLock: 'quick-lock',
} as const;
export type GestureKind = (typeof GestureKind)[keyof typeof GestureKind];

export type GestureCounts = Readonly<Record<GestureKind, number>>;

const ZERO: GestureCounts = {
  [GestureKind.CreateDrag]: 0,
  [GestureKind.CreateClick]: 0,
  [GestureKind.Move]: 0,
  [GestureKind.Resize]: 0,
  [GestureKind.QuickLock]: 0,
};

/** Drag-create hints retire after this many drag creations… */
export const CREATE_DRAG_LEARNED = 3;
/** …or after this many click creations (the user records without dragging). */
export const CREATE_CLICK_LEARNED = 30;
/** Move and resize hints retire after this many committed gestures each. */
export const GESTURE_LEARNED = 3;

function toCount(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
}

export function readGestureCounts(): GestureCounts {
  const raw = readStorage(StorageKey.GestureCount);
  if (raw === null) return ZERO;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return ZERO;
    const record = parsed as Record<string, unknown>;
    return {
      [GestureKind.CreateDrag]: toCount(record[GestureKind.CreateDrag]),
      [GestureKind.CreateClick]: toCount(record[GestureKind.CreateClick]),
      [GestureKind.Move]: toCount(record[GestureKind.Move]),
      [GestureKind.Resize]: toCount(record[GestureKind.Resize]),
      [GestureKind.QuickLock]: toCount(record[GestureKind.QuickLock]),
    };
  } catch {
    return ZERO;
  }
}

/** Count one committed gesture and return the new counts. */
export function recordGesture(kind: GestureKind): GestureCounts {
  const counts = { ...readGestureCounts() };
  counts[kind] += 1;
  writeStorage(StorageKey.GestureCount, JSON.stringify(counts));
  return counts;
}

/** The drag-create hint (ghost second line) is still in its learning period. */
export function createHintActive(counts: GestureCounts): boolean {
  return (
    counts[GestureKind.CreateDrag] < CREATE_DRAG_LEARNED &&
    counts[GestureKind.CreateClick] < CREATE_CLICK_LEARNED
  );
}

export function moveHintActive(counts: GestureCounts): boolean {
  return counts[GestureKind.Move] < GESTURE_LEARNED;
}

export function resizeHintActive(counts: GestureCounts): boolean {
  return counts[GestureKind.Resize] < GESTURE_LEARNED;
}

/** The banner's "double-click to mark continuously" hint retires after this many locks (§4.4). */
export const QUICK_LOCK_LEARNED = 2;

export function quickLockHintActive(counts: GestureCounts): boolean {
  return counts[GestureKind.QuickLock] < QUICK_LOCK_LEARNED;
}

interface GestureCountStore {
  readonly counts: GestureCounts;
  /** Count one committed gesture; every lane subscribed to the store sees the new counts. */
  record(kind: GestureKind): void;
  /** Re-read the device storage (on mounting a lane, or after the local data was cleared). */
  refresh(): void;
}

const sameCounts = (a: GestureCounts, b: GestureCounts): boolean =>
  Object.values(GestureKind).every((kind) => a[kind] === b[kind]);

/**
 * The learning counters as shared state: the seven week-view lanes and the day lane read the
 * same counts, so a gesture committed in one lane retires the hints in all of them at once.
 */
export const useGestureCounts = create<GestureCountStore>()((set, get) => ({
  counts: readGestureCounts(),
  record: (kind) => set({ counts: recordGesture(kind) }),
  refresh: () => {
    const next = readGestureCounts();
    if (!sameCounts(get().counts, next)) set({ counts: next });
  },
}));
